<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import type { AIChatSession, SessionGroup } from '@/types/generated'
  import { AiChatSessionsService } from '@/types/generated/services.gen'
  import { Edit, FolderClosed, FolderOpen, MoreHorizontal, Trash2 } from 'lucide-vue-next'
  import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRoot,
    DropdownMenuSeparator,
    DropdownMenuTrigger
  } from 'radix-vue'
  import AiChatSidebarItem from '../../components/AiChatSidebarItem.vue'

  interface Props {
    folder: SessionGroup
    active?: boolean
    chatCount?: number
    chatSessions?: AIChatSession[]
    activeChatSessionId?: string | null
    activeChatSessionRef?: any
    collapsed?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    active: false,
    chatCount: 0,
    chatSessions: () => [],
    activeChatSessionId: null,
    activeChatSessionRef: null,
    collapsed: false
  })

  const emit = defineEmits<{
    select: [folder: SessionGroup | null]
    delete: [folder: SessionGroup]
    selectChat: [session: AIChatSession]
    rename: [folder: SessionGroup]
    deleteChat: [session: AIChatSession]
    moveChatToFolder: [session: AIChatSession, folder: SessionGroup | null]
    createFolder: []
    moveToFolderFromSidebarItem: [session: AIChatSession, folder: SessionGroup | null]
    createFolderFromSidebarItem: [session: AIChatSession]
  }>()

  const { t } = useTranslation({
    en: {
      Rename: 'Rename',
      Delete: 'Delete',
      DeleteChat: 'Delete chat',
      AddToFolder: 'Add to folder',
      NewFolder: 'New Folder',
      NoFolder: 'No folder'
    },
    de: {
      Rename: 'Umbenennen',
      Delete: 'Löschen',
      DeleteChat: 'Chat löschen',
      AddToFolder: 'Zu Ordner hinzufügen',
      NewFolder: 'Neuer Ordner',
      NoFolder: 'Kein Ordner'
    }
  })

  const handleSelect = () => {
    emit('select', props.folder)
  }

  const handleDelete = () => {
    emit('delete', props.folder)
  }

  const handleRename = () => {
    emit('rename', props.folder)
  }

  const handleDeleteChat = (session: AIChatSession) => {
    emit('deleteChat', session)
  }

  const handleCreateFolder = () => {
    emit('createFolder')
  }

  const handleMoveToFolderFromSidebarItem = async (
    session: AIChatSession,
    folder: SessionGroup | null
  ) => {
    try {
      // Prepare the update data
      const updateData: any = {
        session_group_uuid: folder ? folder.uuid : null
      }

      // If moving to a folder and the current title is "New Chat", update it to use the first message
      if (folder && (!session.title || session.title.trim() === 'New Chat')) {
        const firstUserMessage = session.messages
          .filter((msg) => msg.role === 'user')
          .sort(
            (a, b) =>
              new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime()
          )[0]?.content

        if (firstUserMessage) {
          updateData.title =
            firstUserMessage.length > 50
              ? firstUserMessage.substring(0, 50) + '...'
              : firstUserMessage
        }
      }

      // Update the chat session's folder via API using PATCH
      const updatedSession = await AiChatSessionsService.aiChatSessionsPartialUpdate({
        uuid: session.uuid,
        requestBody: updateData
      })

      // Update the session in the global store
      const globalChatStore = useGlobalChatStore()
      globalChatStore.updateChatSession(updatedSession)

      // Emit the event to parent components
      emit('moveToFolderFromSidebarItem', updatedSession, folder)

      // Select the moved chat session
      emit('selectChat', updatedSession)
    } catch (error) {
      console.error('Failed to move chat to folder:', error)
    }
  }

  const handleCreateFolderFromSidebarItem = (session: AIChatSession) => {
    emit('createFolderFromSidebarItem', session)
  }

  const handleSelectChat = (session: AIChatSession) => {
    // Clear folder selection when selecting a chat session
    emit('select', null)
    emit('selectChat', session)
  }

  // Get global chat store for user data
  const globalChatStore = useGlobalChatStore()
</script>

<template>
  <div
    class="relative flex size-full flex-col content-stretch items-start overflow-x-clip"
    data-cy="session-group-item"
  >
    <!-- Folder Header -->
    <div
      :class="[
        `relative box-border flex w-full shrink-0 cursor-pointer content-stretch items-center justify-between
        overflow-x-clip px-2 py-2`,
        active ? 'border-r-2 border-blue-500 bg-gray-200' : 'bg-white hover:bg-gray-100'
      ]"
      data-cy="folder-header"
      @click="handleSelect"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute bottom-0 left-[-16px] right-[-16px] top-0 border-0 border-x-4
          border-solid border-gray-100"
      />
      <div class="relative flex min-w-0 flex-1 content-stretch items-center gap-2">
        <!-- Folder Icon -->
        <div class="relative flex size-5 shrink-0 items-center justify-center">
          <FolderOpen
            v-if="!collapsed"
            class="h-5 w-5 text-gray-800"
          />
          <FolderClosed
            v-else
            class="h-5 w-5 text-gray-500"
          />
        </div>
        <div
          class="relative flex min-w-0 flex-1 items-center gap-1 text-sm font-medium not-italic text-gray-800"
          :title="`${folder.name}${chatCount > 0 ? ` (${chatCount})` : ''}`"
          data-cy="folder-name"
        >
          <span class="truncate leading-5">{{ folder.name }}</span>
          <span
            v-if="chatCount > 0"
            class="shrink-0 leading-5"
          >
            ({{ chatCount }})
          </span>
        </div>
      </div>
      <DropdownMenuRoot v-if="!folder.is_default">
        <DropdownMenuTrigger as-child>
          <button
            class="relative shrink-0 cursor-pointer rounded-md bg-white"
            data-cy="folder-more-button"
            @click.stop
          >
            <div
              class="relative box-border flex size-full content-stretch items-center justify-center overflow-clip p-1.5"
            >
              <div class="relative size-5 shrink-0 overflow-clip">
                <MoreHorizontal class="h-5 w-5 text-gray-800" />
              </div>
            </div>
            <div
              aria-hidden="true"
              class="pointer-events-none absolute inset-0 rounded-md border border-solid border-gray-200
                shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="z-50 w-[220px] rounded-[12px] border border-gray-200 bg-white p-[6px] shadow-lg"
          align="start"
          side="right"
        >
          <DropdownMenuItem
            class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
              text-gray-800 hover:bg-gray-50"
            data-cy="edit-folder-button"
            @click="handleRename"
          >
            <Edit class="h-5 w-5 text-gray-800" />
            <span class="text-sm leading-5">{{ t('Rename') }}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator class="my-1" />
          <DropdownMenuItem
            class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
              text-red-600 hover:bg-red-50"
            data-cy="delete-folder-button"
            @click="handleDelete"
          >
            <Trash2 class="h-5 w-5 text-red-600" />
            <span class="text-sm leading-5">{{ t('Delete') }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuRoot>
    </div>

    <!-- Chat Sessions List -->
    <div
      v-if="chatSessions.length > 0 && !collapsed"
      class="relative box-border flex w-full shrink-0 flex-col content-stretch items-start overflow-y-auto
        overflow-x-clip py-0"
    >
      <AiChatSidebarItem
        v-for="session in chatSessions"
        :key="session.uuid"
        :data-session-uuid="session.uuid"
        :chat-session="session"
        :in-folder="true"
        @click="handleSelectChat"
        @delete="handleDeleteChat"
        @move-to-folder="handleMoveToFolderFromSidebarItem"
        @create-folder="handleCreateFolderFromSidebarItem"
      />
    </div>
  </div>
</template>
